<script lang="ts" setup>
import {
  useAuth,
  useUser,
  useDrawer,
  useTemplateRef,
  useRouter,
  useCreators,
  computed,
  callOnce,
  watch,
  useFollowers,
} from "#imports";
import { onClickOutside } from "@vueuse/core";
import MPImg from "../widgets/MPImg.vue";
import MPMenu from "./MPMenu.vue";

const authStore = useAuth();
const userStore = useUser();
const creatorStore = useCreators();
const followStore = useFollowers();
const { toggleDrawer, drawerIsOpen } = useDrawer();

const supportedCreatorIds = computed(() =>
  userStore.user?.supports.map((s) => s.creator_id!),
);

const followerIds = computed(() =>
  followStore.followers.map((follower) => follower.creator_id),
);

const data = computed(() => {
  const followers = creatorStore.getByIds(followerIds.value ?? []).value!;

  const supporters = creatorStore.getByIds(
    supportedCreatorIds.value ?? [],
  ).value!;

  return {
    followers,
    supporters,
  };
});

const loadData = async () => {
  await followStore.fetchFollowers();

  await creatorStore.fetchByIds(supportedCreatorIds.value);
  await creatorStore.fetchByIds(followerIds.value);
};

await callOnce(loadData, { mode: "navigation" });

watch(
  () => userStore.user.id,
  async () => {
    await creatorStore.fetchByIds(supportedCreatorIds.value);
    await creatorStore.fetchByIds(followerIds.value);
  },
);

const target = useTemplateRef<HTMLElement>("target");
onClickOutside(target, () => toggleDrawer());

const router = useRouter();
const menuItems: InstanceType<typeof MPMenu>["$props"]["menuItems"] = [
  {
    id: "settings",
    label: "Settings",
    withSlot: false,
    showLabel: false,
    withDivider: true,
    items: [
      {
        label: "Account",
        action: () => router.push("/account"),
      },
      {
        label: "Help & Support",
        action: () => {
          location.href = "mailto:support@mappr.com";
        },
      },
      {
        label: "Creator Studio",
        creatorsOnly: true,
        action: () => router.push("/studio"),
      },
    ],
  },
  {
    id: "supporting",
    label: "Supporting",
    showLabel: true,
    withDivider: false,
    withSlot: true,
    items: [],
  },
  {
    id: "following",
    label: "Following",
    showLabel: true,
    withDivider: false,
    withSlot: true,
    items: [],
  },
];
</script>

<template>
  <ClientOnly>
    <Transition name="slide-in">
      <div v-if="drawerIsOpen" class="drawer" ref="target">
        <button class="close-button" @click="toggleDrawer">
          <i class="ri-close-line" />
        </button>
        <div class="user-info">
          <MPImg
            :src="
              userStore.user.profile_image_url ||
              '/media/images/placeholder-user.webp'
            "
            class="user-image"
            :width="65"
            :height="65"
          />
          <div>
            <h1>{{ userStore.user.mappr_handle || "Your account" }}</h1>
            <span>{{ userStore.user.email }}</span>
          </div>
        </div>
        <MPMenu :menuItems="menuItems">
          <template #supporting>
            <span v-if="data.supporters?.length === 0"
              >You are not supporting any creators</span
            >
            <ul v-else>
              <li v-for="creator in data.supporters" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                  <MPImg
                    :src="
                      creator.profile_image_url ||
                      '/media/images/placeholder-user.webp'
                    "
                    :width="35"
                    :height="35"
                  />
                  @{{ creator.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template>
          <template #following>
            <span v-if="data.followers?.length === 0"
              >You are not supporting any creators</span
            >
            <ul v-else>
              <li v-for="creator in data.followers" :key="creator.id">
                <NuxtLinkLocale :to="`/@${creator.mappr_handle}`">
                  <MPImg
                    :src="
                      creator.profile_image_url ||
                      '/media/images/placeholder-user.webp'
                    "
                    :width="35"
                    :height="35"
                  />
                  @{{ creator.mappr_handle }}
                </NuxtLinkLocale>
              </li>
            </ul>
          </template>
        </MPMenu>
        <nav class="actions">
          <NuxtLinkLocale
            @click="
              authStore.signOut();
              toggleDrawer();
            "
            style="cursor: pointer"
          >
            Sign out
          </NuxtLinkLocale>
        </nav>
      </div>
    </Transition>
  </ClientOnly>
</template>

<style lang="postcss" scoped>
.drawer {
  position: fixed;
  display: flex;
  flex-direction: column;
  top: 0;
  right: 0;
  width: 20rem;
  height: 100dvh;
  background-color: var(--color-slate-50);
  z-index: 8002;
  color: var(--color-slate-700);
  box-shadow: var(--shadow-md);
  padding: 0.65rem;
}

.slide-in-enter-active,
.slide-in-leave-active {
  transition: all 150ms ease-in-out;
}

.slide-in-enter-from,
.slide-in-leave-to {
  transform: translateX(100%);
}

.close-button {
  position: absolute;
  top: 0.5rem;
  right: 0.25rem;
  background-color: transparent;
  border: none;
  cursor: pointer;
  color: var(--color-slate-700);
  font-size: 1.65rem;
}

.user-info {
  display: flex;
  padding: 1rem;
  align-items: top;
  gap: 0.75rem;
}

.user-info > span {
  font-size: 1.25rem;
  margin: 0;
  padding: 0 0 0.5rem 0;
  line-height: 1;
}

.user-info h1 {
  font-size: 1.25rem;
  margin: 0;
  padding: 0.25rem 0 0;
}

.user-info div {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.user-image {
  border-radius: 100%;
  object-fit: cover;
  width: 60px;
  height: 60px;
}

nav {
  padding: 0 1rem;
}
nav h2 {
  font-size: 1rem;
  color: var(--color-slate-400);
}

nav + nav {
  padding-top: 1rem;
}

nav ul {
  margin: 0;
  padding: 0;
  list-style: none;
}

nav ul li a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

nav ul li img {
  width: 35px;
  height: 35px;
  object-fit: cover;
  border-radius: 100%;
  border: 2px solid white;
  box-shadow: var(--shadow-base);
}

nav.actions {
  margin-top: auto;
  display: flex;
  justify-content: space-between;
  padding: 1rem;
  font-size: 1rem;
}

nav.actions i {
  font-size: 1.65rem;
  color: var(--color-slate-50);
}

nav.actions a {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  color: var(--color-slate-400);
}

.studio {
  position: relative;
  padding: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  position: relative;
  color: var(--color-slate-100);
}

.studio i {
  font-size: 2rem;
}
</style>
